/**
 * Image optimization utilities for Supabase images
 */

export interface ImageOptimizationConfig {
  quality: {
    high: number      // For above-the-fold images
    standard: number  // For below-the-fold images
    blur: number      // For blur placeholders
  }
  formats: {
    primary: 'webp' | 'avif' | 'jpeg' | 'png'
    fallback: 'jpeg' | 'png'
    supported: ('webp' | 'avif' | 'jpeg' | 'png')[]
  }
  sizes: {
    mobile: {
      small: number
      medium: number
      large: number
    }
    tablet: {
      small: number
      medium: number
      large: number
    }
    desktop: {
      small: number
      medium: number
      large: number
    }
  }
  breakpoints: {
    mobile: number
    tablet: number
    desktop: number
  }
}

// Default optimization configuration
export const DEFAULT_IMAGE_CONFIG: ImageOptimizationConfig = {
  quality: {
    high: 85,      // Above-the-fold images
    standard: 75,  // Below-the-fold images
    blur: 20       // Blur placeholders
  },
  formats: {
    primary: 'webp',
    fallback: 'jpeg',
    supported: ['webp', 'avif', 'jpeg', 'png']
  },
  sizes: {
    mobile: {
      small: 320,
      medium: 480,
      large: 640
    },
    tablet: {
      small: 640,
      medium: 768,
      large: 1024
    },
    desktop: {
      small: 1024,
      medium: 1280,
      large: 1920
    }
  },
  breakpoints: {
    mobile: 768,
    tablet: 1024,
    desktop: 1280
  }
}

/**
 * Device capability detection for optimal image format selection
 */
export class DeviceCapabilityDetector {
  private static instance: DeviceCapabilityDetector
  private capabilities: {
    webp: boolean
    avif: boolean
    connectionSpeed: 'slow' | 'fast' | 'unknown'
  } | null = null

  static getInstance(): DeviceCapabilityDetector {
    if (!DeviceCapabilityDetector.instance) {
      DeviceCapabilityDetector.instance = new DeviceCapabilityDetector()
    }
    return DeviceCapabilityDetector.instance
  }

  async detectCapabilities(): Promise<{
    webp: boolean
    avif: boolean
    connectionSpeed: 'slow' | 'fast' | 'unknown'
  }> {
    if (this.capabilities) {
      return this.capabilities
    }

    const [webpSupport, avifSupport, connectionSpeed] = await Promise.all([
      this.checkWebPSupport(),
      this.checkAVIFSupport(),
      this.detectConnectionSpeed()
    ])

    this.capabilities = {
      webp: webpSupport,
      avif: avifSupport,
      connectionSpeed
    }

    return this.capabilities
  }

  private async checkWebPSupport(): Promise<boolean> {
    return new Promise((resolve) => {
      const webP = new Image()
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2)
      }
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
    })
  }

  private async checkAVIFSupport(): Promise<boolean> {
    return new Promise((resolve) => {
      const avif = new Image()
      avif.onload = avif.onerror = () => {
        resolve(avif.height === 2)
      }
      avif.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A='
    })
  }

  private async detectConnectionSpeed(): Promise<'slow' | 'fast' | 'unknown'> {
    // Use Network Information API if available
    if ('connection' in navigator) {
      const connection = (navigator as { connection?: { effectiveType?: string } }).connection
      if (connection) {
        const effectiveType = connection.effectiveType
        if (effectiveType === 'slow-2g' || effectiveType === '2g') {
          return 'slow'
        } else if (effectiveType === '3g' || effectiveType === '4g') {
          return 'fast'
        }
      }
    }

    // Fallback: simple timing test using a small static asset instead of image proxy
    try {
      const startTime = performance.now()
      await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache'
      })
      const endTime = performance.now()
      
      return (endTime - startTime) > 1000 ? 'slow' : 'fast'
    } catch {
      return 'unknown'
    }
  }

  getOptimalFormat(): 'webp' | 'avif' | 'jpeg' {
    if (!this.capabilities) {
      return 'webp' // Default fallback
    }

    // Prefer AVIF for fast connections if supported
    if (this.capabilities.avif && this.capabilities.connectionSpeed === 'fast') {
      return 'avif'
    }

    // Use WebP if supported
    if (this.capabilities.webp) {
      return 'webp'
    }

    // Fallback to JPEG
    return 'jpeg'
  }

  getOptimalQuality(priority: boolean = false): number {
    if (!this.capabilities) {
      return priority ? DEFAULT_IMAGE_CONFIG.quality.high : DEFAULT_IMAGE_CONFIG.quality.standard
    }

    const baseQuality = priority ? DEFAULT_IMAGE_CONFIG.quality.high : DEFAULT_IMAGE_CONFIG.quality.standard

    // Reduce quality for slow connections
    if (this.capabilities.connectionSpeed === 'slow') {
      return Math.max(baseQuality - 15, 50)
    }

    return baseQuality
  }
}

/**
 * Generate responsive image sizes string for different breakpoints
 */
export function generateResponsiveSizes(
  config: ImageOptimizationConfig = DEFAULT_IMAGE_CONFIG,
  sizeVariant: 'small' | 'medium' | 'large' = 'medium'
): string {
  const { sizes, breakpoints } = config
  
  return [
    `(max-width: ${breakpoints.mobile}px) ${sizes.mobile[sizeVariant]}px`,
    `(max-width: ${breakpoints.tablet}px) ${sizes.tablet[sizeVariant]}px`,
    `${sizes.desktop[sizeVariant]}px`
  ].join(', ')
}

/**
 * Generate optimized image URL for Supabase storage
 */
export function generateOptimizedImageUrl(
  originalUrl: string,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'webp' | 'avif' | 'jpeg' | 'png'
    blur?: boolean
    fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside'
    useProxy?: boolean
  } = {}
): string {
  // For local images (starting with /), return as-is
  if (originalUrl.startsWith('/')) {
    return originalUrl
  }

  if (!isSupabaseStorageUrl(originalUrl)) {
    return originalUrl
  }

  // Always use proxy for Supabase images to avoid 402 errors
  const params = new URLSearchParams({
    url: encodeURIComponent(originalUrl),
    f: options.format || 'webp',
    q: (options.quality || DEFAULT_IMAGE_CONFIG.quality.standard).toString()
  })

  if (options.width) params.set('w', options.width.toString())
  if (options.height) params.set('h', options.height.toString())
  if (options.blur) params.set('blur', 'true')
  if (options.fit) params.set('fit', options.fit)

  return `/api/image-proxy?${params.toString()}`
}

/**
 * Generate fallback URL strategy for Supabase images
 * Returns array of URLs to try in order of preference
 */
export function generateImageFallbackUrls(
  originalUrl: string,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'webp' | 'avif' | 'jpeg' | 'png'
  } = {}
): string[] {
  // For local images, return as-is
  if (originalUrl.startsWith('/')) {
    return [originalUrl]
  }

  if (!isSupabaseStorageUrl(originalUrl)) {
    return [originalUrl]
  }

  const urls: string[] = []

  // 1. Try optimized proxy URL
  urls.push(generateOptimizedImageUrl(originalUrl, { ...options, useProxy: true }))

  // 2. Try with different format if WebP/AVIF was requested
  if (options.format === 'webp' || options.format === 'avif') {
    urls.push(generateOptimizedImageUrl(originalUrl, { ...options, format: 'jpeg' }))
  }

  // 3. Fallback to original URL (may cause 402 with Next.js transformations)
  urls.push(originalUrl)

  return urls
}

/**
 * Generate multiple optimized URLs for different formats and sizes
 */
export function generateImageSrcSet(
  originalUrl: string,
  sizes: number[],
  options: {
    format?: 'webp' | 'avif' | 'jpeg' | 'png'
    quality?: number
  } = {}
): string {
  if (!isSupabaseStorageUrl(originalUrl)) {
    return originalUrl
  }

  return sizes
    .map(size => {
      const optimizedUrl = generateOptimizedImageUrl(originalUrl, {
        width: size,
        ...options
      })
      return `${optimizedUrl} ${size}w`
    })
    .join(', ')
}

/**
 * Generate blur placeholder data URL
 */
export async function generateBlurPlaceholder(
  originalUrl: string,
  size: number = 40
): Promise<string | undefined> {
  if (!isSupabaseStorageUrl(originalUrl)) {
    return undefined
  }

  try {
    const blurUrl = generateOptimizedImageUrl(originalUrl, {
      width: size,
      height: size,
      quality: DEFAULT_IMAGE_CONFIG.quality.blur,
      blur: true
    })

    const response = await fetch(blurUrl)
    if (!response.ok) return undefined

    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => resolve(undefined)
      reader.readAsDataURL(blob)
    })
  } catch {
    return undefined
  }
}

/**
 * Check if URL is from Supabase storage
 */
export function isSupabaseStorageUrl(url: string): boolean {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.includes('.supabase.co') && 
           urlObj.pathname.includes('/storage/v1/object/')
  } catch {
    return false
  }
}

/**
 * Extract bucket and path from Supabase storage URL
 */
export function parseSupabaseStorageUrl(url: string): { bucket: string; path: string } | null {
  try {
    const urlObj = new URL(url)
    const pathParts = urlObj.pathname.split('/storage/v1/object/public/')
    if (pathParts.length !== 2) return null

    const [bucket, ...pathSegments] = pathParts[1].split('/')
    return {
      bucket,
      path: pathSegments.join('/')
    }
  } catch {
    return null
  }
}

/**
 * Performance monitoring for image loading
 */
export class ImagePerformanceMonitor {
  private static metrics: Map<string, {
    loadTime: number
    size: number
    format: string
    cached: boolean
  }> = new Map()

  static recordImageLoad(
    url: string,
    loadTime: number,
    size: number,
    format: string,
    cached: boolean = false
  ) {
    this.metrics.set(url, {
      loadTime,
      size,
      format,
      cached
    })
  }

  static getMetrics() {
    return Array.from(this.metrics.entries()).map(([url, metrics]) => ({
      url,
      ...metrics
    }))
  }

  static getAverageLoadTime(): number {
    const metrics = Array.from(this.metrics.values())
    if (metrics.length === 0) return 0
    
    const totalTime = metrics.reduce((sum, metric) => sum + metric.loadTime, 0)
    return totalTime / metrics.length
  }

  static getCacheHitRate(): number {
    const metrics = Array.from(this.metrics.values())
    if (metrics.length === 0) return 0
    
    const cachedCount = metrics.filter(metric => metric.cached).length
    return cachedCount / metrics.length
  }

  static clear() {
    this.metrics.clear()
  }
}